import React from 'react';
import type { DocumentMarkupPoint, DocumentMarkupStroke } from '../../types';
import {
  ENG_CALLOUT_FILL,
  STAMP_BASE_PX,
  STAMP_ROTATE_GAP_PX,
  calloutBoxPx,
  cloudRect,
  pointsToPath,
  revisionCloudPath,
  stampHalfPx,
  strokePolyline,
  wrapLabel,
} from './engineeringMarkup';
import { FastenerSymbol } from './FastenerSymbol';
import { getFastener } from './fastenerLibrary';

interface PageScale {
  sx: number;
  sy: number;
}

function PixelGroup({
  x,
  y,
  scale,
  rotation = 0,
  children,
}: {
  x: number;
  y: number;
  scale: PageScale;
  rotation?: number;
  children: React.ReactNode;
}) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale.sx} ${scale.sy}) rotate(${rotation})`}>
      {children}
    </g>
  );
}

function CalloutShape({
  points,
  color,
  fill,
  textColor,
  text,
  strokeWidth,
  scale,
  draft,
}: {
  points: DocumentMarkupPoint[];
  color: string;
  fill?: string;
  textColor?: string;
  text?: string;
  strokeWidth: number;
  scale: PageScale;
  draft?: boolean;
}) {
  if (points.length < 1) return null;
  const pin = points[0];
  const box = points[points.length - 1] || pin;
  const leader = pointsToPath(points);
  const label = text || (draft ? '' : 'Note');
  const { w, h } = calloutBoxPx(label || 'Note');
  const lines = wrapLabel(label || (draft ? '' : 'Note'));
  const ink = textColor || color;

  return (
    <g className="markup-callout">
      <path
        d={leader}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <PixelGroup x={pin.x} y={pin.y} scale={scale}>
        <circle r={3.2} fill={color} />
      </PixelGroup>
      <PixelGroup x={box.x} y={box.y} scale={scale}>
        <rect
          x={-w / 2}
          y={-h / 2}
          width={w}
          height={h}
          fill={fill || ENG_CALLOUT_FILL}
          stroke={color}
          strokeWidth={1.4}
        />
        {lines.map((line, i) => (
          <text
            key={`${i}-${line}`}
            x={0}
            y={(i - (lines.length - 1) / 2) * 16}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={ink}
            fontSize={13}
            fontFamily='Georgia, "Iowan Old Style", Palatino, "Times New Roman", serif'
            fontWeight={600}
            style={{ pointerEvents: 'none' }}
          >
            {line}
          </text>
        ))}
      </PixelGroup>
    </g>
  );
}

function TypewriterShape({
  point,
  color,
  textColor,
  text,
  scale,
  draft,
}: {
  point: DocumentMarkupPoint;
  color: string;
  textColor?: string;
  text?: string;
  scale: PageScale;
  draft?: boolean;
}) {
  const label = text || (draft ? '' : 'Note');
  const { w, h } = calloutBoxPx(label || 'Note');
  const lines = wrapLabel(label || (draft ? '' : 'Note'));
  const ink = textColor || color;
  return (
    <g className="markup-typewriter">
      <PixelGroup x={point.x} y={point.y} scale={scale}>
        <rect
          x={-w / 2}
          y={-h / 2}
          width={w}
          height={h}
          fill="rgba(255,255,255,0.92)"
          stroke={color}
          strokeWidth={1.2}
        />
        {lines.map((line, i) => (
          <text
            key={`${i}-${line}`}
            x={0}
            y={(i - (lines.length - 1) / 2) * 16}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={ink}
            fontSize={13}
            fontFamily='ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif'
            fontWeight={500}
            style={{ pointerEvents: 'none' }}
          >
            {line}
          </text>
        ))}
      </PixelGroup>
    </g>
  );
}

export function MarkupShape({
  stroke,
  scale,
  onDoubleClick,
}: {
  stroke: DocumentMarkupStroke;
  scale: PageScale;
  onDoubleClick?: (stroke: DocumentMarkupStroke) => void;
}) {
  const handleDbl = (e: React.MouseEvent) => {
    if (!onDoubleClick) return;
    e.stopPropagation();
    onDoubleClick(stroke);
  };

  if (stroke.tool === 'stamp' && stroke.points[0] && stroke.stampId) {
    const p = stroke.points[0];
    const size = STAMP_BASE_PX * (stroke.stampScale ?? 1);
    const def = getFastener(stroke.stampId);
    return (
      <g onDoubleClick={handleDbl} style={{ pointerEvents: onDoubleClick ? 'auto' : 'none' }}>
        <PixelGroup x={p.x} y={p.y} scale={scale} rotation={stroke.stampRotation ?? 0}>
          <g transform={`translate(${-size / 2} ${-size / 2})`}>
            <FastenerSymbol
              id={stroke.stampId}
              size={size}
              color={stroke.color}
              label={stroke.text || def?.shortLabel}
              showLabel
            />
          </g>
        </PixelGroup>
      </g>
    );
  }

  if (stroke.tool === 'callout') {
    return (
      <g onDoubleClick={handleDbl} style={{ pointerEvents: onDoubleClick ? 'auto' : 'none', cursor: onDoubleClick ? 'text' : undefined }}>
        <CalloutShape
          points={stroke.points}
          color={stroke.color}
          fill={stroke.fill}
          textColor={stroke.textColor}
          text={stroke.text}
          strokeWidth={stroke.strokeWidth}
          scale={scale}
        />
      </g>
    );
  }

  if (stroke.tool === 'cloud' && stroke.points.length >= 2) {
    return (
      <path
        className="markup-cloud"
        d={revisionCloudPath(stroke.points[0], stroke.points[1])}
        stroke={stroke.color}
        strokeWidth={stroke.strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    );
  }

  if (stroke.tool === 'typewriter' && stroke.points[0]) {
    return (
      <g onDoubleClick={handleDbl} style={{ pointerEvents: onDoubleClick ? 'auto' : 'none', cursor: onDoubleClick ? 'text' : undefined }}>
        <TypewriterShape
          point={stroke.points[0]}
          color={stroke.color}
          textColor={stroke.textColor}
          text={stroke.text}
          scale={scale}
        />
      </g>
    );
  }

  if (stroke.tool === 'line' && stroke.points.length >= 2) {
    return (
      <path
        d={pointsToPath(stroke.points)}
        stroke={stroke.color}
        strokeWidth={stroke.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        vectorEffect="non-scaling-stroke"
      />
    );
  }

  if (stroke.tool === 'zigzag' && stroke.points.length >= 2) {
    return (
      <path
        d={pointsToPath(strokePolyline(stroke))}
        stroke={stroke.color}
        strokeWidth={stroke.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="miter"
        fill="none"
        vectorEffect="non-scaling-stroke"
      />
    );
  }

  const isHi = stroke.tool === 'highlighter';
  return (
    <path
      d={pointsToPath(stroke.points)}
      stroke={stroke.color}
      strokeWidth={stroke.strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity={isHi ? 0.38 : 1}
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function DraftMarkupShape({
  tool,
  points,
  color,
  fill,
  text,
  strokeWidth,
  stampId,
  stampScale,
  stampRotation,
  scale,
}: {
  tool: DocumentMarkupStroke['tool'];
  points: DocumentMarkupPoint[];
  color: string;
  fill?: string;
  text?: string;
  strokeWidth: number;
  stampId?: string;
  stampScale?: number;
  stampRotation?: number;
  scale: PageScale;
}) {
  const draft: DocumentMarkupStroke = {
    documentId: '',
    documentUrl: '',
    leadId: '',
    pageNumber: 1,
    tool,
    color,
    strokeWidth,
    points,
    userId: '',
    userName: '',
    createdAt: new Date(),
    fill,
    text,
    stampId,
    stampScale,
    stampRotation,
  };
  if (tool === 'callout') {
    return (
      <CalloutShape
        points={points}
        color={color}
        fill={fill}
        text={text}
        strokeWidth={strokeWidth}
        scale={scale}
        draft
      />
    );
  }
  if (tool === 'typewriter' && points[0]) {
    return <TypewriterShape point={points[0]} color={color} textColor={color} text={text} scale={scale} draft />;
  }
  if (tool === 'cloud' && points.length >= 2) {
    return (
      <path
        d={revisionCloudPath(points[0], points[1])}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        opacity={0.85}
      />
    );
  }
  return <MarkupShape stroke={draft} scale={scale} />;
}

export function SelectedMarkupHalo({
  stroke,
  scale,
}: {
  stroke: DocumentMarkupStroke;
  scale: PageScale;
}) {
  if (stroke.tool === 'stamp') return null;
  if (stroke.tool === 'callout' || stroke.tool === 'typewriter') {
    const box = stroke.tool === 'typewriter' ? stroke.points[0] : stroke.points[stroke.points.length - 1];
    if (!box) return null;
    const { w, h } = calloutBoxPx(stroke.text || '');
    return (
      <PixelGroup x={box.x} y={box.y} scale={scale}>
        <rect
          x={-w / 2 - 5}
          y={-h / 2 - 5}
          width={w + 10}
          height={h + 10}
          fill="none"
          stroke="#2563eb"
          strokeWidth={1.25}
          strokeDasharray="4 3"
        />
      </PixelGroup>
    );
  }
  if (stroke.tool === 'cloud' && stroke.points.length >= 2) {
    const { x0, y0, w, h } = cloudRect(stroke.points[0], stroke.points[1]);
    return (
      <rect
        x={x0 - 1.2}
        y={y0 - 1.2}
        width={w + 2.4}
        height={h + 2.4}
        fill="none"
        stroke="#2563eb"
        strokeWidth={0.45}
        strokeDasharray="1.2 0.8"
      />
    );
  }
  if (stroke.points.length < 2) return null;
  return (
    <path
      d={pointsToPath(strokePolyline(stroke))}
      stroke="#2563eb"
      strokeWidth={Math.max(4, (stroke.strokeWidth || 1.2) + 3)}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity={0.3}
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function StampTransformBox({
  stroke,
  scale,
}: {
  stroke: DocumentMarkupStroke;
  scale: PageScale;
}) {
  const p = stroke.points[0];
  if (!p) return null;
  const half = stampHalfPx(stroke.stampScale ?? 1);
  const rotY = -half - STAMP_ROTATE_GAP_PX;
  const corners = [
    { x: -half, y: -half },
    { x: half, y: -half },
    { x: half, y: half },
    { x: -half, y: half },
  ];
  return (
    <PixelGroup x={p.x} y={p.y} scale={scale} rotation={stroke.stampRotation ?? 0}>
      <rect
        x={-half}
        y={-half}
        width={half * 2}
        height={half * 2}
        fill="none"
        stroke="#2563eb"
        strokeWidth={1.25}
        strokeDasharray="4 3"
      />
      <line x1={0} y1={-half} x2={0} y2={rotY} stroke="#2563eb" strokeWidth={1.15} />
      <circle cx={0} cy={rotY} r={5} fill="#fff" stroke="#2563eb" strokeWidth={1.4} />
      {corners.map(c => (
        <rect
          key={`${c.x},${c.y}`}
          x={c.x - 4.5}
          y={c.y - 4.5}
          width={9}
          height={9}
          fill="#fff"
          stroke="#2563eb"
          strokeWidth={1.4}
        />
      ))}
    </PixelGroup>
  );
}
